/**
 * The CRM's verdict on each deal's broker, on the live book (UI-05).
 *
 * GET /loans carries `brokerId` and nothing of what the CRM holds against it,
 * so a deal a blocklisted broker brought read exactly like any other on the
 * Pipeline. The grid's Exceptions rule (`loanExceptions`) reads
 * `loan.brokerBlocklisted`; this attaches it from `GET /brokers/`, once per
 * page, the way the open triggers are attached (`useLoansWithLiveTriggers`).
 *
 * A role that may not read the CRM is not asked and a 403 is not retried; the
 * loans then pass through unchanged, with no flag — never a reassuring "clear".
 * The server's own view grid reads the same blocklist for a scheduled report
 * (view_grid.grid_rows), so the two agree.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { isAzureAuthEnabled } from '@/auth/authMode'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { msalInstance } from '@/auth/msalInstance'
import api from '@/services/api'
import type { Loan } from '@/types'

/** Local JWT or an MSAL account — do not ask the CRM before a bearer exists
 *  (the rule `useDemoDataAccess.isLiveBookAuthReady` applies to GET /loans;
 *  restated here so this hook has no import the page tests stub away). */
function authReadyFor(localToken: string | null): boolean {
  if (localToken) return true
  if (!isAzureAuthEnabled) return true
  return Boolean(msalInstance && (msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]))
}

export const BROKER_STATUS_QUERY_KEY = ['brokers', 'status'] as const

export type BrokerStatus = { blocklisted: boolean; preferred: boolean }

type BrokerRow = { id: string; is_blocklisted?: boolean | null; is_preferred?: boolean | null }

/** {broker id: status} for every broker the CRM lists. */
export function brokerStatusById(rows: BrokerRow[]): Map<string, BrokerStatus> {
  const out = new Map<string, BrokerStatus>()
  for (const row of rows) {
    if (!row || typeof row.id !== 'string') continue
    out.set(row.id, { blocklisted: Boolean(row.is_blocklisted), preferred: Boolean(row.is_preferred) })
  }
  return out
}

/** The loans with the CRM's broker verdict attached; a loan whose broker the
 *  CRM does not list is returned as it was. */
export function attachBrokerStatus(loans: Loan[], status: Map<string, BrokerStatus>): Loan[] {
  if (status.size === 0) return loans
  let changed = false
  const next = loans.map((loan) => {
    const verdict = loan.brokerId ? status.get(loan.brokerId) : undefined
    if (!verdict) return loan
    if (loan.brokerBlocklisted === verdict.blocklisted && loan.brokerPreferred === verdict.preferred) return loan
    changed = true
    return { ...loan, brokerBlocklisted: verdict.blocklisted, brokerPreferred: verdict.preferred }
  })
  return changed ? next : loans
}

function useBrokerStatusRows(enabled: boolean) {
  const { localToken, localUser } = useLocalAuth()
  const authReady = authReadyFor(localToken)
  // Presentation only — the API is authoritative. Unknown permissions mean
  // "ask the API" rather than assume a refusal.
  const actions = localUser?.permissions?.crm
  const roleMaySee = Boolean(localUser?.is_super_admin) || !actions || actions.includes('view')
  const query = useQuery({
    queryKey: BROKER_STATUS_QUERY_KEY,
    // Trailing slash matches FastAPI's route; `/brokers` 307s and a browser
    // following the bounce can drop the bearer.
    queryFn: async () => (await api.get('/brokers/')).data as BrokerRow[],
    enabled: enabled && authReady && roleMaySee,
    staleTime: 5 * 60_000,
    retry: (failures, error) =>
      !(isAxiosError(error) && error.response?.status === 403) && failures < 2,
  })
  return Array.isArray(query.data) ? query.data : null
}

/** The live book with `brokerBlocklisted` / `brokerPreferred` on each deal
 *  whose broker the CRM lists. A demo book (`isLive` false) passes through. */
export function useLoansWithBrokerStatus(loans: Loan[], isLive: boolean): Loan[] {
  const rows = useBrokerStatusRows(isLive)
  const status = useMemo(() => brokerStatusById(rows ?? []), [rows])
  return useMemo(() => (isLive && rows ? attachBrokerStatus(loans, status) : loans), [isLive, loans, rows, status])
}
